/* eslint-disable no-unused-expressions */
/* eslint-disable no-nested-ternary */

import React, { useState } from 'react';
import {
  defineMessages,
  FormattedMessage,
  FormattedNumber,
  injectIntl,
} from 'react-intl';
import Activity from 'brastrap/common/activity/Activity';
import Message from 'brastrap/common/message/Message';
import FieldSet from 'brastrap/common/fieldset/Fieldset';
import Icon from 'brastrap/common/icon/Icon';
import FieldSelect from 'brastrap/common/field-select/FieldSelect';
import DetailsSummary from 'brastrap/transactional/details-summary/DetailsSummary';
import BagItem from '../bag/BagItem';

/**
 * Show return details summary
 *
 * @param {Object} props
 * @returns {String|XML}
 */
const ReturnsDetailSummary = (props: Props) => {
  const {
    onRequestReturn,
    isLoading,
    requestReturnErrorMsg,
    isSmallWindowWidth,
    result,
    itemsToReturn,
    setItemsToReturn,
    intl: { formatMessage },
  } = props;

  const [trackingNumber, setTrackingNumber] = useState(null);
  const [showTrackingNumberError, setShowTrackingNumberError] = useState(false);
  const [showToolTip, setShowToolTip] = useState(false);
  const [showContent, setShowContent] = useState(true);

  const messages = defineMessages({
    processing: {
      id: 'returns.processing-request',
      defaultMessage: 'Your request is being processed...',
    },
    ctaLabel: {
      id: 'returns.return-these-items-cta-label',
      defaultMessage: 'Return these items',
    },
    emptyBagLabel: {
      id: 'returns.empty-bag-label',
      defaultMessage: 'Add items to your return',
    },
    returnsSummaryTitle: {
      id: 'returns.returns-summary-title',
      defaultMessage: 'Returns summary',
    },
    trackingNumber: {
      id: 'returns.tracking-number-label',
      defaultMessage: 'Tracking number',
    },
    trackingNumberError: {
      id: 'returns.tracking-number-error',
      defaultMessage: 'Please select a tracking number.',
    },
    trackingNumberErrorTitle: {
      id: 'returns.tracking-number-error-title',
      defaultMessage: 'Details missing',
    },
    trackingNumberToolTip: {
      id: 'returns.tracking-number-tool-tip',
      defaultMessage:
        'The tracking number is a 22 digit number shown on the bottom of the First Class Return Service sticker. This sticker is on the Order Summary paperwork within your parcel.',
    },
  });

  /**
   * Builds tracking number options
   * @returns {Array}
   */
  function buildTrackingNumberOptions() {
    return (
      result &&
      result.returnTrackingCodes
        .filter(code => !code.isUsed)
        .map(code => ({ label: code.id, value: code.id }))
    );
  }

  /**
   * Removes selected item from bag
   * @param {Object} item
   */
  function removeItemFromReturnsBag(item) {
    const newArr = itemsToReturn.filter(itemToReturn => {
      return itemToReturn.orderItemId !== item.orderItemId;
    });
    setItemsToReturn(newArr);
  }

  /**
   * Maps bag items to object for posting
   * @param {String} orderId
   * @returns {Object}
   */
  function mapItemsToReturnToRequestBody(orderId) {
    return {
      parcelId: trackingNumber,
      orderId: orderId,
      items: getItemsToReturnWithQuantity().map(item => ({
        orderItemId: item.orderItemId,
        quantity: parseInt(item.quantity, 10),
      })),
    };
  }

  /**
   * Gets all items from bag that have quantity
   * @returns {Array}
   */
  function getItemsToReturnWithQuantity() {
    return itemsToReturn.filter(item => item.quantity !== 0);
  }

  /**
   * Returns true if bag is empty or has all items with quantity missing
   * @returns {Array}
   */
  function isItemsToReturnEmptyOrIncomplete() {
    return (
      itemsToReturn.length === 0 ||
      itemsToReturn.every(item => item.quantity === 0)
    );
  }

  const spinner = (
    <div className="c-checkout-page__spinner">
      <p>{formatMessage(messages.processing)}</p>
      <Activity />
    </div>
  );

  return (
    <DetailsSummary
      onClickToToggleContent={() => setShowContent(x => !x)}
      isCollapsable={isSmallWindowWidth}
      showCollapsableContent={showContent || showTrackingNumberError}
      itemCount={getItemsToReturnWithQuantity()
        .map(item => parseInt(item.quantity, 10))
        .reduce((prev, curr) => prev + curr, 0)}
      title={formatMessage(messages.returnsSummaryTitle)}
      actions={[
        {
          label: formatMessage(messages.ctaLabel),
          modifiers: ['full-width'],
          onClick: () => {
            trackingNumber
              ? onRequestReturn(
                  result.orderId,
                  mapItemsToReturnToRequestBody(result.orderId)
                )
              : setShowTrackingNumberError(true);
          },
          disabled: isItemsToReturnEmptyOrIncomplete() || isLoading,
        },
      ]}
    >
      <table className="c-bag c-bag--is-editable" summary="Items in bag">
        <tbody className="c-bag__main">
          {isLoading ? (
            spinner
          ) : isItemsToReturnEmptyOrIncomplete() ? (
            <p className="c-message--inform c-message__main">
              {formatMessage(messages.emptyBagLabel)}
            </p>
          ) : (
            getItemsToReturnWithQuantity().map(item => (
              <div className="bagItemDelete">
                <BagItem
                  currency={result.currency}
                  item={{
                    code: null,
                    colour: item.colour,
                    id: null,
                    imgUrl: item.image,
                    isAccessory: null,
                    price: item.price,
                    quantity: item.quantity,
                    size: { size: item.size },
                    skuCode: null,
                    styleCode: item.styleColourCode,
                    styleName: item.styleName,
                    colourSwatch: item.colourSwatch,
                    brandName: item.brandName,
                    url: null,
                  }}
                />
                <td>
                  <button
                    className="c-bag__remove"
                    onClick={() => removeItemFromReturnsBag(item)}
                    type="button"
                  >
                    <span className="c-icon">
                      <svg
                        className="c-icon__glyph"
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 200 200"
                        role="img"
                      >
                        <path
                          fillRule="evenodd"
                          d="M84 24H22c-8 0-14 6-14 14s6 14 14 14h156c8 0 14-6
                  14-14s-6-14-14-14h-62V14c0-8-6-14-14-14h-4c-8 0-14 6-14 14v10zM24 64h152v120c0 9-7 16-16
                  16H40c-9 0-16-7-16-16V64zm28 0h96v104c0 2-2 4-4 4H56c-2 0-4-2-4-4V64zm12 0h16v96H64V64zm28
                  0h16v96H92V64zm28 0h16v96h-16V64z"
                        />
                      </svg>
                      <span className="u-hidden">
                        <FormattedMessage
                          id="bag.remove"
                          defaultMessage="Remove"
                        />
                      </span>
                    </span>
                  </button>
                </td>
              </div>
            ))
          )}
          <div className="returns-item-select__trackingNumberContainer">
            <button
              className="returns-item-select__infoIcon"
              onClick={() => setShowToolTip(x => !x)}
              type="button"
            >
              <Icon icon="information" />
            </button>
            <div className="returns-item-select__trackingNumberDropDownContainer">
              <FieldSet>
                <FieldSelect
                  onChange={ev => {
                    setTrackingNumber(ev.target.value);
                    setShowTrackingNumberError(false);
                  }}
                  label={formatMessage(messages.trackingNumber)}
                  placeholder={'Tracking Number'}
                  options={buildTrackingNumberOptions()}
                  required
                  value={trackingNumber}
                  id="trackingNumber"
                />
              </FieldSet>
            </div>
          </div>
          {showToolTip ? (
            <div className="returns-item-select__trackingNumberToolTipMsg">
              <Message
                id="toolTip"
                modifiers={['inform', 'banner']}
                onClose={() => setShowToolTip(false)}
                dismissable
              >
                {formatMessage(messages.trackingNumberToolTip)}
              </Message>
            </div>
          ) : (
            <></>
          )}
        </tbody>
        <tfoot className="c-bag__footer">
          <tr className="c-bag__total c-bag__grand__total">
            <td className="c-bag__total__value" colSpan={'2'}>
              <FormattedMessage
                id="returns.to-be-refunded-label"
                defaultMessage="Total: "
              />
              <strong>
                <FormattedNumber
                  value={itemsToReturn
                    .map(item => item.value)
                    .reduce((prev, curr) => prev + curr, 0)}
                  currency={result.currency}
                  style="currency"
                />
              </strong>
            </td>
          </tr>
          {showTrackingNumberError && itemsToReturn.length > 0 ? (
            <div className="returns-item-select__trackingNumberErrorContainer">
              <Message
                id="errorMessage"
                title={formatMessage(messages.trackingNumberErrorTitle)}
                modifiers={['warn', 'banner']}
                onClose={() => setShowTrackingNumberError(false)}
                dismissable
              >
                {formatMessage(messages.trackingNumberError)}
              </Message>
            </div>
          ) : (
            <></>
          )}

          {requestReturnErrorMsg ? (
            <Message
              id="errorMessage"
              title={'Error'}
              modifiers={['warn', 'banner']}
            >
              {requestReturnErrorMsg}
            </Message>
          ) : (
            <></>
          )}
        </tfoot>
      </table>
    </DetailsSummary>
  );
};

ReturnsDetailSummary.propTypes = {};

ReturnsDetailSummary.defaultProps = {};

export default injectIntl(ReturnsDetailSummary);
